import React from 'react'
import { IconButton, Typography, Theme } from '@material-ui/core'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import AddIcon from '@material-ui/icons/Add'
import {
    AddTemplateVendorParams,
    UpdateTemplateVendorParams,
    DeleteTemplateVendorParams,
} from '../../../hooks/useEzSchedule/types'
import {
    TemplateVendor,
    BaseWorkorder,
    ezScheduleDetails,
} from '../../../models'
import { formatTotals } from '../services'
import { getTotalPriceExpense } from '../../../models/WorkOrder/services'
import {
    HeaderValue,
    IconValue,
    TextValue,
    TotalsMoneyValue,
    TotalsTextValue,
} from '../styles'
import { Container } from '../../../components'
import { usePopover } from '../../../hooks/usePopover'
import { SelectorPopover } from '../../../components/TemplateScheduleGantt/Components/SelectorPopover/SelectorPopover'
import { VendorTableRow } from './VendorTableRow'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    theme: Theme
    ezSchedule: ezScheduleDetails
    templateVendors: TemplateVendor[]
    workorders: BaseWorkorder[]
    portfolio?: boolean
    addTemplateVendor: (params: AddTemplateVendorParams) => void
    updateTemplateVendor: (params: UpdateTemplateVendorParams) => void
    deleteTemplateVendor: (params: DeleteTemplateVendorParams) => void
}

export const VendorTable = (props: Props) => {
    const {
        theme,
        templateVendors,
        workorders,
        ezSchedule,
        addTemplateVendor,
        updateTemplateVendor,
        deleteTemplateVendor,
    } = props
    const popoverState = usePopover()

    // Create vendor list from workorders
    const workorderVendors = React.useMemo(() => {
        const vendors: {
            [vendorId: number]: {
                id: number
                name: string
                forecast: number
                invoiced: number
            }
        } = {}

        workorders.forEach((wo) => {
            if (wo.vendor_id && wo.vendor_name) {
                if (!vendors[wo.vendor_id]) {
                    vendors[wo.vendor_id] = {
                        id: wo.vendor_id,
                        name: wo.vendor_name,
                        forecast: 0,
                        invoiced: 0,
                    }
                }
                // Add to forecast
                vendors[wo.vendor_id].forecast += getTotalPriceExpense(wo)
                // Add to invoiced if available
                if (
                    wo.invoiced_price !== undefined &&
                    wo.invoiced_price !== null
                ) {
                    vendors[wo.vendor_id].invoiced += wo.invoiced_price
                }
            }
        })

        return Object.values(vendors)
    }, [workorders])

    // Calculate vendor totals
    const vendorTotals = React.useMemo(() => {
        const totals: {
            [vendorId: number]: {
                name: string
                budget: number
                forecast: number
                variance: number
                invoiced: number
            }
        } = {}

        // Calculate vendor budgets from template vendors
        templateVendors.forEach((tv) => {
            if (!totals[tv.vendor.id]) {
                totals[tv.vendor.id] = {
                    name: tv.vendor.name,
                    budget: 0,
                    forecast: 0,
                    variance: 0,
                    invoiced: 0,
                }
            }
            totals[tv.vendor.id].budget += Number(tv.budget || 0)
        })

        // Calculate forecasts and invoiced amounts from workorders
        workorders.forEach((wo) => {
            if (wo.vendor_id && totals[wo.vendor_id]) {
                // Add to forecast
                totals[wo.vendor_id].forecast += getTotalPriceExpense(wo)

                // Add to invoiced if available
                if (
                    wo.invoiced_price !== undefined &&
                    wo.invoiced_price !== null
                ) {
                    totals[wo.vendor_id].invoiced += wo.invoiced_price
                }
            }
        })

        // Calculate variances
        Object.values(totals).forEach((vendor) => {
            vendor.variance = vendor.budget - vendor.forecast
        })

        return totals
    }, [templateVendors, workorders])

    // Calculate grand totals
    const grandTotals = React.useMemo(() => {
        return Object.values(vendorTotals).reduce(
            (acc, vendor) => ({
                budget: acc.budget + vendor.budget,
                forecast: acc.forecast + vendor.forecast,
                variance: acc.variance + vendor.variance,
                invoiced: acc.invoiced + vendor.invoiced,
            }),
            { budget: 0, forecast: 0, variance: 0, invoiced: 0 },
        )
    }, [vendorTotals])

    // Get vendors that have workorders but aren't in template
    const availableVendors = React.useMemo(() => {
        const templateVendorIds = new Set(
            templateVendors.map((tv) => tv.vendor.id),
        )
        return workorderVendors.filter(
            (vendor) => !templateVendorIds.has(vendor.id),
        )
    }, [workorderVendors, templateVendors])

    return (
        <Box sx={{ flexGrow: 1, padding: 2 }}>
            <Container
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    minHeight: 40,
                }}
            >
                {/* Title */}
                <Container
                    style={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <Typography
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                        }}
                    >
                        Vendors
                    </Typography>
                </Container>
            </Container>
            <Grid container spacing={0}>
                {/* Header Row */}
                <Grid item xs={0.5}>
                    <HeaderValue>&nbsp;</HeaderValue>
                </Grid>
                <Grid item xs={3.5}>
                    <HeaderValue
                        style={{
                            justifyContent: 'flex-start',
                        }}
                    >
                        Vendor
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Budget</HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Forecast</HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Variance</HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Invoiced</HeaderValue>
                </Grid>

                {/* Template Vendor Rows */}
                {templateVendors.map((tv) => (
                    <VendorTableRow
                        key={`TEMPLATE-VENDOR-${tv.id}`}
                        {...props}
                        templateVendor={tv}
                        forecastedVendorTotal={
                            vendorTotals[tv.vendor.id]?.forecast || 0
                        }
                        invoicedVendorTotal={
                            vendorTotals[tv.vendor.id]?.invoiced || 0
                        }
                        updateTemplateVendor={updateTemplateVendor}
                        deleteTemplateVendor={deleteTemplateVendor}
                    />
                ))}

                {/* Add Vendor Row */}
                <Grid item xs={0.5}>
                    <IconValue>
                        <IconButton onClick={popoverState.handleOpen}>
                            <AddIcon />
                        </IconButton>
                    </IconValue>
                    <SelectorPopover
                        {...props}
                        popoverState={popoverState}
                        data={availableVendors}
                        label="Select Vendor"
                        onSubmit={(selectedId) => {
                            addTemplateVendor({
                                vendorId: selectedId,
                                scheduleId: ezSchedule.id,
                                portfolio: props.portfolio,
                            })
                        }}
                    />
                </Grid>
                <Grid item xs={3.5}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>

                {/* Totals Row */}
                <Grid item xs={0.5}>
                    <TotalsTextValue>&nbsp;</TotalsTextValue>
                </Grid>
                <Grid item xs={3.5}>
                    <TotalsTextValue>Totals</TotalsTextValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(grandTotals.budget)}
                    </TotalsMoneyValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(grandTotals.forecast)}
                    </TotalsMoneyValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(grandTotals.variance)}
                    </TotalsMoneyValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(grandTotals.invoiced)}
                    </TotalsMoneyValue>
                </Grid>
            </Grid>
        </Box>
    )
}
